import {
  IPostProjectSetDailyQuery,
  useInfinitePostProjectSetDailyQuery,
  useUploadSetQuery,
} from "@smp/graphql";
import { dataArrayToHashMap } from "@smp/utils";
import produce from "immer";
import { useState } from "react";
import {
  InfiniteData,
  UseInfiniteQueryOptions,
  useQueryClient,
} from "react-query";

import { gqlClient } from "@/hooks/use_graphql";

const UPLOAD_REFETCH_INTERVAL = 1000;

export const useQueryPostProjectSetDaily = (
  variables: Parameters<typeof useInfinitePostProjectSetDailyQuery>[2],
  options?: Omit<
    UseInfiniteQueryOptions<IPostProjectSetDailyQuery>,
    "queryFn" | "queryKey" | "getNextPageParam"
  >,
) => {
  const queryClient = useQueryClient();

  const [uploadIdsInProcessing, setUploadIdsInProcessing] = useState<number[]>(
    [],
  );

  useUploadSetQuery(
    gqlClient,
    {
      id__in: uploadIdsInProcessing,
    },
    {
      refetchInterval: UPLOAD_REFETCH_INTERVAL,
      enabled: !!uploadIdsInProcessing.length,
      onSuccess: (data) => {
        const uploadsNormalized = dataArrayToHashMap(data.upload_set, "id");

        setUploadIdsInProcessing((state) =>
          state.filter((id) => uploadsNormalized[id].is_processing),
        );

        queryClient.setQueriesData<InfiniteData<IPostProjectSetDailyQuery>>(
          useInfinitePostProjectSetDailyQuery.getKey(variables),
          produce((draft) => {
            draft?.pages.forEach((page) => {
              page.post_project_set_daily.edges.forEach((postProject) => {
                postProject.node.uploads.forEach((upload, idx) => {
                  const updatedData = uploadsNormalized[upload.id];

                  if (updatedData) {
                    postProject.node.uploads[idx] = {
                      ...upload,
                      is_processing: updatedData.is_processing,
                      url: updatedData.url,
                      thumb: updatedData.thumb,
                    };
                  }
                });
              });
            });
          }),
        );
      },
    },
  );

  return useInfinitePostProjectSetDailyQuery("cursor", gqlClient, variables, {
    getNextPageParam: (currentPage) => {
      const { cursor_next } = currentPage.post_project_set_daily.page_info;
      return cursor_next ? { cursor: cursor_next } : undefined;
    },
    ...options,
    onSuccess: (...args) => {
      const [data] = args;

      const uploadIds: number[] = [];

      data.pages.forEach((page) => {
        page.post_project_set_daily.edges.forEach((postProject) => {
          postProject.node.uploads.forEach((upload) => {
            if (upload.is_processing) {
              uploadIds.push(upload.id);
            }
          });
        });
      });

      setUploadIdsInProcessing((state) =>
        [...state, ...uploadIds].filter(
          (item, idx, self) => self.indexOf(item) === idx,
        ),
      );

      options?.onSuccess?.(...args);
    },
  });
};

useQueryPostProjectSetDaily.getKey = useInfinitePostProjectSetDailyQuery.getKey;
